/**
 * Optimized Swatch Badge System
 * Handles "New Shade" badges for product variants
 * Consolidated from embedded HTML to improve INP performance
 */

class SwatchBadgeManager {
  constructor() {
    this.initialized = false;
    this.variantMetafields = {};
    this.currentDate = new Date();
    
    // Bind methods to maintain context
    this.init = this.init.bind(this);
    this.addSwatchBadge = this.addSwatchBadge.bind(this);
    this.processAllBadges = this.processAllBadges.bind(this);
  }

  /**
   * Initialize the swatch badge system
   * Collects variant metafields and processes badges
   */
  init() {
    if (this.initialized) return;
    
    // Collect variant metafields from the page data
    this.collectVariantMetafields();
    
    // Process all badges on the page
    this.processAllBadges();
    
    this.initialized = true;
  }

  /**
   * Collect variant metafields from window.variantMetafields
   * This data will be populated by the product template
   */
  collectVariantMetafields() {
    if (window.variantMetafields) {
      this.variantMetafields = window.variantMetafields;
    }
  }

  /**
   * Process all swatch badge containers on the page
   * Replaces the original addSwatchBadge functionality
   */
  processAllBadges() {
    const badgeContainers = document.querySelectorAll('.product-badge-container--swatch');
    
    badgeContainers.forEach(container => {
      const variantId = container.getAttribute('data-variant-id');
      const metafieldValue = this.variantMetafields[variantId]?.show_new_shade_badge_until;
      
      if (metafieldValue) {
        const badgeDate = new Date(metafieldValue);
        
        if (badgeDate > this.currentDate) {
          this.displayNewVariantShadesBadge(container, metafieldValue);
        }
      }
    });
  }

  /**
   * Create a swatch badge element
   * @param {string} badgeContent - The content to display in the badge
   * @param {string} badgeType - The type of badge ('text', etc.)
   * @returns {string} HTML string for the badge
   */
  createSwatchBadge(badgeContent, badgeType) {
    let badgeHtml = '';
    
    if (badgeType === 'text') {
      badgeHtml = `<div class="product-badge"><span>${badgeContent}</span></div>`;
    }
    
    return badgeHtml;
  }

  /**
   * Display a "New Shade" badge for a variant
   * @param {HTMLElement} badgeContainer - The container to add the badge to
   * @param {string} newVariantShadesBadge - The date string for the badge
   */
  displayNewVariantShadesBadge(badgeContainer, newVariantShadesBadge) {
    const badgeDate = new Date(newVariantShadesBadge);
    
    // Get the badge label from the global translations or fallback
    const badgeLabel = window.swatchBadgeTranslations?.newShadeVariantBadge || 'NEW';
    
    if (badgeDate > this.currentDate) {
      // Only add badge if one doesn't already exist
      if (!badgeContainer.querySelector('.product-badge')) {
        const productBadgeHtml = this.createSwatchBadge(badgeLabel, 'text');
        badgeContainer.insertAdjacentHTML('beforeend', productBadgeHtml);
      }
    }
  }

  /**
   * Legacy function name for backwards compatibility
   * Maps to processAllBadges for existing calls
   */
  addSwatchBadge() {
    this.processAllBadges();
  }
}

const swatchBadgeManager = new SwatchBadgeManager();

// Expose the legacy global function for backwards compatibility
window.addSwatchBadge = function() {
  swatchBadgeManager.addSwatchBadge();
};

// Auto-initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  swatchBadgeManager.init();
});

// Re-process badges when variants change (for AJAX updates)
if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
  subscribe(PUB_SUB_EVENTS.variantChange, function() {
    swatchBadgeManager.processAllBadges();
  });
}

// Export for potential module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = SwatchBadgeManager;
}